import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth-store";
import { isSelectTenant, type LoginResponse, type LoginSuccess } from "@/types/api";
import * as api from "./api";

/** 公开配置 (部署模式 / 短信 / 注册 / 计费开关) */
export function usePublicConfig() {
  return useQuery({
    queryKey: ["public-config"],
    queryFn: api.fetchPublicConfig,
    staleTime: 5 * 60_000,
  });
}

/** 登录成功写 store; select_tenant 原样返回给调用方弹公司选择 */
function useStoreSession() {
  const setSession = useAuthStore((s) => s.setSession);
  const qc = useQueryClient();
  return (data: LoginResponse) => {
    if (isSelectTenant(data)) return;
    setSession(data.access_token, data.refresh_token, data.user);
    // 换了账号: 旧用户的缓存 (含 ["me"]) 全部作废
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "public-config" });
  };
}

/** 登录 mutation: 成功后写入 auth-store, 调用方按 role 决定跳转目标 */
export function useLogin() {
  const store = useStoreSession();
  return useMutation({ mutationFn: api.login, onSuccess: store });
}

export function useSmsLogin() {
  const store = useStoreSession();
  return useMutation({ mutationFn: api.loginSms, onSuccess: store });
}

export function useSelectTenant() {
  const store = useStoreSession();
  return useMutation({
    mutationFn: api.selectTenant,
    onSuccess: (d: LoginSuccess) => store(d),
  });
}

export function useRegister() {
  const store = useStoreSession();
  return useMutation({
    mutationFn: api.register,
    onSuccess: (d: LoginSuccess) => store(d),
  });
}

export function useSendSms() {
  return useMutation({ mutationFn: api.sendSms });
}

/** 退出 (本地立即清空 + 后端 jti 拉黑) */
export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  const qc = useQueryClient();
  return useMutation({
    // 旧版页面与 React 共用同一个会话 (React 登录时写入旧版 key), 退出即两边都退出:
    // 后端拉黑 jti, 本地连旧版 key 一起删 (clear({ logout: true }))。
    mutationFn: () => api.logout(),
    onSettled: () => {
      clear({ logout: true });
      qc.clear();
    },
  });
}

/** 取当前用户 (有 token 时才请求), 并把余额 / 公司类型等字段合并进 auth-store */
export function useCurrentUser() {
  const token = useAuthStore((s) => s.token);
  const patchUser = useAuthStore((s) => s.patchUser);
  const q = useQuery({
    queryKey: ["me"],
    queryFn: api.fetchMe,
    enabled: !!token,
    staleTime: 60_000,
  });
  const me = q.data;
  useEffect(() => {
    if (!me) return;
    patchUser({
      display_name: me.display_name,
      role: me.role,
      company_id: me.company_id,
      company_name: me.company_name,
      company_type: me.company_type,
      phone: me.phone,
      email: me.email,
      credits_enabled: me.credits_enabled,
      credits_fen: me.credits_fen,
      credits: me.credits,
    });
  }, [me, patchUser]);
  return q;
}

/** 改密码 */
export function useChangePassword() {
  return useMutation({ mutationFn: api.changePassword });
}
